import { WhiteCard } from 'components/styles_components/card';
import { StyledLink } from 'components/styles_components/link';
import {
  CardOptionTypography,
  CardOptionHintTypography,
  CardTitleTypography,
} from 'components/styles_components/typography';
import { Grid, ButtonGroup, Box, Table, TableBody } from 'components/ui';
import { FormattedMessage } from 'react-intl';
import { InspectionSubjectLink, PlaceLink } from 'utils/link';

import Date from 'components/date';
import InfoContainer from 'components/info_container';
import CancelButton from 'components/inspection/button/cancel';
import DeleteButton from 'components/inspection/button/delete';
import EditButton from 'components/inspection/button/edit';
import ExecuteButton from 'components/inspection/button/execute';
import ReportButton from 'components/inspection/button/report';
import Inspector from 'components/inspection/inspector';
import ComplianceRatioCard from 'components/inspection/show_item/compliance_ratio_card';
import PeriodCard from 'components/inspection/show_item/period_card';
import ResultCard from 'components/inspection/show_item/result_card';
import StatusCard from 'components/inspection/show_item/status_card';
import InspectionFieldsListIndex from 'components/inspection_field/index';
import ShowItemTableRow from 'components/item/show_item_table_row';
import Inspection from 'utils/inspection';

const ShowItem = ({ inspection, space, refetch, leftColumChildren }) => {
  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, lg: 9 }}>
        <Grid size={{ xs: 12 }}>
          <WhiteCard className={Inspection(inspection).htmlClass}>
            <CardTitleTypography variant="h5">{inspection.name}</CardTitleTypography>
            <Grid container spacing={2} sx={{ mt: 2, mb: 2 }}>
              <StatusCard inspection={inspection} />
              <ComplianceRatioCard
                complianceRatio={inspection.complianceRatio}
                label={<FormattedMessage id="inspection.compliance_ratio.description" />} />
              <PeriodCard periodNumber={inspection.periodNumber} periodCycle={inspection.periodCycle} />
              <ResultCard result={inspection.result} doneAt={inspection.doneAt} />
            </Grid>
            <Grid container spacing={2} sx={{ mt: 2, mb: 2 }}>
              <Grid size={{ xs: 12 }}>
                <Table>
                  <TableBody>
                    <ShowItemTableRow label={<FormattedMessage id="inspection.results.total" />}>
                      {inspection.inspectionResultsTotal ?? 0}
                    </ShowItemTableRow>
                    <ShowItemTableRow label={<FormattedMessage id="inspector" />}>
                      <Inspector inspector={inspection.inspector} />
                    </ShowItemTableRow>
                    <ShowItemTableRow label={<FormattedMessage id="inspection.due_date" />}>
                      <Date
                        date={inspection.dueDate}
                        warning={inspection.incoming}
                        error={inspection.overdue}
                      />
                    </ShowItemTableRow>
                  </TableBody>
                </Table>
              </Grid>
              {inspection.notes && <Grid size={{ xs: 12 }}>
                <CardOptionTypography>
                  <FormattedMessage id="inspection.notes" />
                </CardOptionTypography>
                <CardOptionHintTypography>
                  {inspection.notes}
                </CardOptionHintTypography>
              </Grid>
              }
            </Grid>
            {space.adminRole && (
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <DeleteButton inspection={inspection} />
                <ButtonGroup>
                  <CancelButton inspection={inspection} refetch={refetch} />
                  <ReportButton inspection={inspection} />
                  <EditButton inspection={inspection} refetch={refetch} />
                </ButtonGroup>
              </Box>
            )}
          </WhiteCard>
        </Grid>
        <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
          <WhiteCard>
            <CardOptionTypography>
              <FormattedMessage id="place" />
            </CardOptionTypography>
            <Table>
              <TableBody>
                <ShowItemTableRow label={<FormattedMessage id="place.name" />}>
                  {inspection.place.name}
                </ShowItemTableRow>
                <ShowItemTableRow label={<FormattedMessage id="place.street" />}>
                  {inspection.place.street}
                </ShowItemTableRow>
                <ShowItemTableRow label={<FormattedMessage id="place.city" />}>
                  {inspection.place.city}
                </ShowItemTableRow>
              </TableBody>
            </Table>
            <Box display="flex" justifyContent="flex-end">
              <StyledLink to={PlaceLink(inspection.place.id)}>
                <FormattedMessage id="button.place.view" />
              </StyledLink>
            </Box>
          </WhiteCard>
        </Grid>
        {inspection.inspectionSubject && (
          <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
            <WhiteCard>
              <CardOptionTypography>
                <FormattedMessage id="inspection_subject" />
              </CardOptionTypography>
              <Table>
                <TableBody>
                  <ShowItemTableRow label={<FormattedMessage id="inspection_subject.brand" />}>
                    {inspection.inspectionSubject.brand || '-'}
                  </ShowItemTableRow>
                  <ShowItemTableRow label={<FormattedMessage id="inspection_subject.model" />}>
                    {inspection.inspectionSubject.model || '-'}
                  </ShowItemTableRow>
                  <ShowItemTableRow
                    label={<FormattedMessage id="inspection_subject.serial_number" />}>
                    {inspection.inspectionSubject.serialNumber || '-'}
                  </ShowItemTableRow>
                </TableBody>
              </Table>
              <Box display="flex" justifyContent="flex-end">
                <StyledLink to={InspectionSubjectLink(inspection.inspectionSubject.id)}>
                  <FormattedMessage id="button.inspection_subject.view" />
                </StyledLink>
              </Box>
            </WhiteCard>
          </Grid>
        )}
        {leftColumChildren}
      </Grid>
      <Grid size={{ xs: 12, lg: 3 }} sx={{ display: 'flex' }}>
        <Grid container direction="column" spacing={2} sx={{ flex: 1 }}>
          <Grid size={{ xs: 12 }}>
            <WhiteCard className="green-border" style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardOptionTypography>
                <FormattedMessage id="inspection.due_date" />
              </CardOptionTypography>
              <Box sx={{ mt: 2, mb: 2, flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Date date={inspection.dueDate} warning={inspection.incoming} error={inspection.overdue} />
              </Box>
              {space.inspectorRole && (
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ExecuteButton inspection={inspection} space={space} refetch={refetch} />
                </Box>
              )}
            </WhiteCard>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <WhiteCard>
              <CardOptionTypography>
                <FormattedMessage id="inspection.field.count" />
              </CardOptionTypography>
              {inspection.inspectionFields.length > 0 ? (
                <Table>
                  <TableBody>
                    <InspectionFieldsListIndex
                      showValues={false}
                      inspectionFields={inspection.inspectionFields}
                    />
                  </TableBody>
                </Table>
              ) : (
                <Box
                  sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <InfoContainer>
                    <FormattedMessage id="inspection.fields.empty" />
                  </InfoContainer>
                </Box>
              )}
            </WhiteCard>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default ShowItem;
